// src/components/PurchaseFlow/PurchaseFlow.js

import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClientContext } from '../../context/ClientContext';
import { SelectedOfferProvider } from '../../context/SelectedOfferContext';
import { SuggestedDataProvider } from '../../context/SuggestedDataContext';
import { PurchaseFlowStateContext } from '../../context/PurchaseFlowStateContext';

// Importar todos los componentes de pasos
import OfferStep from './Steps/Offer/OfferStep';
import DataEntryStep from './Steps/DataEntry/DataEntryStep';
import CustomerHandoffStep from './Steps/CustomerHandoff/CustomerHandoffStep';
import DocumentUploadStep from './Steps/DocumentUpload/DocumentUploadStep';
import ConfirmationStep from './Steps/ConfirmationStep';
import PaymentStep from './Steps/Payment/PaymentStep';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { trackSaleStarted, trackSaleAbandoned, trackSaleStepEntered } from '../../analytics/events';
import { useAnalyticsContext } from '../../analytics/useAnalyticsContext';
import SaleFlowErrorBoundary from '../SaleFlowErrorBoundary';

import './PurchaseFlow.css';

const EXIT_WARNING_MESSAGE = 'Si salis de esta venta, el progreso cargado se perdera.';

const PurchaseFlow = ({ onClose, onCloseToSales, onSaleCompleted }) => {
  const { clientData, clientStatus } = useContext(ClientContext);
  const { updateSaleMonitor } = useContext(PurchaseFlowStateContext);
  const { commerceId, branchId, channel } = useAnalyticsContext();
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);
  const [stepLabels, setStepsLabels] = useState([]);
  const [purchaseRequestId, setPurchaseRequestId] = useState(null);
  const [lastStepStatus, setLastStepStatus] = useState('pending');
  const lastStepStatusRef = useRef('pending');
  const exitGuardStateAddedRef = useRef(false);
  const bypassExitGuardRef = useRef(false);

  const [formData, setFormData] = useState({});
  const saleStartedTrackedRef = useRef(false);
  const [showExitWarningModal, setShowExitWarningModal] = useState(false);
  const [pendingExitAction, setPendingExitAction] = useState(null);
  const isFlowTerminal = (
    (steps.length > 0 && currentStepIndex === steps.length - 1 && lastStepStatus === 'success') ||
    lastStepStatus === 'error'
  );
  const shouldWarnOnExit = !isFlowTerminal;

  const clientPurchaseFlowMap = {
    NEW: process.env.REACT_APP_PURCHASE_FLOW_NEW,
    RENEWER: process.env.REACT_APP_PURCHASE_FLOW_RENEWER,
    OLD: process.env.REACT_APP_PURCHASE_FLOW_OLD,
    RESTRICTED: process.env.REACT_APP_PURCHASE_FLOW_RESTRICTED,
    BLACKLISTED: process.env.REACT_APP_PURCHASE_FLOW_BLACKLISTED
  };  

  // Track sale started when PurchaseFlow mounts (only once per flow instance)
  useEffect(() => {
    if (commerceId && clientData?.id && !saleStartedTrackedRef.current) {
      trackSaleStarted({
        commerceId,
        branchId,
        availableCredit: clientData.available_credit || 0,
        currency: 'ARS',
        customerDni: clientData?.dni
      });
      saleStartedTrackedRef.current = true;
    }
  }, [commerceId, branchId, clientData?.id, clientData?.dni]);

  // Track sale abandoned when PurchaseFlow closes without completion
  // Note: This will fire on unmount, but we rely on lastStepStatusRef to determine
  // if the sale was completed. If lastStepStatusRef.current is 'success', the sale was completed.
  useEffect(() => {
    return () => {
      // Only track abandonment if the sale wasn't completed
      // This cleanup runs when component unmounts (flow closed)
      if (commerceId && lastStepStatusRef.current !== 'success') {
        trackSaleAbandoned({
          commerceId,
          branchId,
          reason: 'flow_closed',
          customerDni: clientData?.dni
        });
      }
    };
  }, [commerceId, branchId]);

  useEffect(() => {
    if (!shouldWarnOnExit) {
      return undefined;
    }

    const openExitWarningModal = (action) => {
      setPendingExitAction(action);
      setShowExitWarningModal(true);
    };

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = EXIT_WARNING_MESSAGE;
    };

    const handlePopState = () => {
      if (bypassExitGuardRef.current) {
        bypassExitGuardRef.current = false;
        return;
      }

      window.history.pushState({ purchaseFlowExitGuard: true }, '', window.location.href);
      exitGuardStateAddedRef.current = true;
      openExitWarningModal('browser_back');
    };

    window.history.pushState({ purchaseFlowExitGuard: true }, '', window.location.href);
    exitGuardStateAddedRef.current = true;
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);

      // Remove the extra history entry added for the back-button guard
      // without changing the visible route when the flow closes normally.
      if (exitGuardStateAddedRef.current && window.history.state?.purchaseFlowExitGuard) {
        exitGuardStateAddedRef.current = false;
        window.history.back();
      }
    };
  }, [navigate, shouldWarnOnExit]);

  useEffect(() => {
    if (!showExitWarningModal) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowExitWarningModal(false);
        setPendingExitAction(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showExitWarningModal]);

  useEffect(() => {
    const getStepsForClientStatus = () => {
      let stepsConfig = '';
      stepsConfig = clientPurchaseFlowMap[clientStatus];

      if (!stepsConfig) {
        console.error(`No hay configuración de pasos para el estado ${clientStatus}`);
        return;
      }

      const stepsArray = stepsConfig.split(',').map((stepName) => {
        const trimmedStepName = stepName.trim();
        switch (trimmedStepName) {
          case 'OfferStep':
            return OfferStep;
          case 'DataEntryStep':
            return DataEntryStep;
          case 'CustomerHandoffStep':
            return CustomerHandoffStep;
          case 'DocumentUploadStep':
            return DocumentUploadStep;
          case 'ConfirmationStep':
            return ConfirmationStep;
          case 'PaymentStep':
            return PaymentStep;
          default:
            console.warn(`Paso desconocido: "${trimmedStepName}"`);
            return null;
        }
      });

      const stepLabelsArray = stepsConfig.split(',').map((stepName) => {
        const trimmedStepName = stepName.trim();
        switch (trimmedStepName) {
          case 'OfferStep':
            return 'Producto';
          case 'DataEntryStep':
            return 'Datos Personales';
          case 'CustomerHandoffStep':
            return 'Autogestión';
          case 'DocumentUploadStep':
            return 'Documentos';
          case 'ConfirmationStep':
            return 'Confirmación';
          case 'PaymentStep':
            return 'Medio de Pago';
          default:
            console.warn(`Etiqueta desconocida para paso: "${trimmedStepName}"`);
            return null;
        }
      });

      setSteps(stepsArray);
      setStepsLabels(stepLabelsArray);
    };

    getStepsForClientStatus();

  }, [clientStatus]);

  const handleNext = (data, stepStatus = null) => {
    setFormData((prevData) => {
      const updatedData = {
        ...prevData,
        ...data,
      };
      return updatedData;
    });

    if (currentStepIndex === steps.length - 1 && stepStatus) {
      setLastStepStatus(stepStatus);
      lastStepStatusRef.current = stepStatus;
      if (stepStatus === 'success') {
        onSaleCompleted?.();
      }
    } else {
      setCurrentStepIndex((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStepIndex((prevStep) => prevStep - 1);
  };

  const handleFlowTerminalStatus = useCallback((status) => {
    setLastStepStatus(status);
    lastStepStatusRef.current = status;
  }, []);

  // Publicar paso actual al monitor de venta (sidebar)
  useEffect(() => {
    if (updateSaleMonitor && steps.length > 0) {
      const stepKey = stepLabels[currentStepIndex] || null;
      updateSaleMonitor({ stepIndex: currentStepIndex, stepKey });
    }
  }, [updateSaleMonitor, currentStepIndex, steps.length, stepLabels]);

  // Track step entered
  useEffect(() => {
    if (commerceId && steps.length > 0 && currentStepIndex < steps.length) {
      const stepName = stepLabels[currentStepIndex] || `Step${currentStepIndex + 1}`;
      // Map step labels to step names
      const stepNameMap = {
        'Producto': 'OfferStep',
        'Datos Personales': 'DataEntryStep',
        'Autogestión': 'CustomerHandoffStep',
        'Documentos': 'DocumentUploadStep',
        'Confirmación': 'ConfirmationStep',
        'Medio de Pago': 'PaymentStep'
      };
      const mappedStepName = stepNameMap[stepName] || stepName;
      
      trackSaleStepEntered({
        commerceId,
        branchId,
        stepName: mappedStepName,
        customerDni: clientData?.dni
      });
    }
  }, [commerceId, branchId, currentStepIndex, steps.length, stepLabels, clientData?.dni]);

  const renderStep = () => {
    const CurrentStepComponent = steps[currentStepIndex];

    if (!CurrentStepComponent) {
      console.error(`Componente para el paso index ${currentStepIndex} no está definido.`);
      return <div>Error al cargar el paso. Por favor, intenta nuevamente.</div>;
    }

    return (
      <CurrentStepComponent
        clientData={clientData}
        purchaseRequestId={purchaseRequestId}
        setPurchaseRequestId={setPurchaseRequestId}
        onNext={handleNext}
        onBack={handleBack}
        onClose={onClose}
        onCloseToSales={onCloseToSales}
        onFlowTerminalStatus={handleFlowTerminalStatus}
        formData={formData}
      />
    );
  };

  // Función para calcular el progreso
  const calculateProgress = () => {
    const totalSteps = steps.length;
    if (totalSteps === 0) return 0;

    // Si el último paso está en success, retornamos 100%
    if (currentStepIndex === totalSteps - 1 && lastStepStatus === 'success') {
      return 100;
    }

    // Calculamos el progreso basado en los pasos completados
    const completedSteps = currentStepIndex;
    return (completedSteps / (totalSteps - 1)) * 100;
  };

  const handleCloseExitWarningModal = () => {
    setShowExitWarningModal(false);
    setPendingExitAction(null);
  };

  const handleConfirmExit = () => {
    const action = pendingExitAction;
    handleCloseExitWarningModal();

    if (action === 'browser_back') {
      bypassExitGuardRef.current = true;
      navigate(-1);
    }
  };

  return (
    <SelectedOfferProvider>
      <SuggestedDataProvider>
        <div className="purchase-flow rg-sale-flow">

          {/* Stepper con barra de progreso - oculto en confirmación o error terminal */}
          {!(currentStepIndex === steps.length - 1 && lastStepStatus === 'success') && lastStepStatus !== 'error' && (
            <div className="stepper-container rg-sale-flow__stepper">
              <div className="stepper-wrapper rg-stepper">
                {steps.map((_, index) => {
                  let stepClassName = 'stepper-item rg-stepper__item';
                  if (index === currentStepIndex) {
                    if (index === steps.length - 1 && lastStepStatus === 'success') {
                      stepClassName += ' completed rg-stepper__item--completed';
                    } else {
                      stepClassName += ' active rg-stepper__item--active';
                    }
                  } else if (index < currentStepIndex) {
                    stepClassName += ' completed rg-stepper__item--completed';
                  }

                  return (
                    <div className={stepClassName} key={index}>
                      <div className="step-counter rg-stepper__icon">
                        {(index < currentStepIndex ||
                          (index === steps.length - 1 && lastStepStatus === 'success')) ? (
                          <FontAwesomeIcon icon={faCheck} />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <p className="step-label rg-stepper__name">{stepLabels[index]}</p>
                    </div>
                  );
                })}
              </div>
              <div className="progress-bar-wrapper">
                <div
                  className="progress-bar"
                  style={{
                    width: `${calculateProgress()}%`
                  }}
                ></div>
              </div>
            </div>
          )}

          {/* Renderizar el componente del paso actual */}
          <SaleFlowErrorBoundary key={currentStepIndex}>
            {renderStep()}
          </SaleFlowErrorBoundary>

          {showExitWarningModal && (
            <>
              <div
                className="modal-backdrop fade show rg-modal-backdrop rg-modal-backdrop--seller"
                onClick={handleCloseExitWarningModal}
                aria-hidden="true"
              ></div>
              <div
                className="modal fade text-left w-100 show rg-modal rg-modal--seller rg-sale-flow__exit-modal"
                tabIndex="-1"
                style={{ display: 'block' }}
                aria-modal="true"
                role="dialog"
              >
                <div className="modal-dialog modal-dialog-centered rg-modal__dialog rg-modal__dialog--sm">
                  <div className="modal-content rg-modal__content">
                    <div className="modal-header rg-modal__header">
                      <h6 className="modal-title rg-modal__title">Salir de la venta</h6>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={handleCloseExitWarningModal}
                        aria-label="Cerrar"
                      ></button>
                    </div>
                    <div className="modal-body rg-modal__body rg-sale-flow__exit-modal-body">
                      <div className="rg-sale-flow__exit-modal-icon" aria-hidden="true">
                        <i className="bi bi-exclamation-triangle"></i>
                      </div>
                      <p className="rg-modal__body-text rg-sale-flow__exit-modal-text">
                        {EXIT_WARNING_MESSAGE}
                      </p>
                    </div>
                    <div className="modal-footer rg-modal__actions">
                      <button
                        type="button"
                        className="btn btn-outline-secondary rg-modal__action-btn"
                        onClick={handleCloseExitWarningModal}
                      >
                        Continuar venta
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary rg-modal__action-btn"
                        onClick={handleConfirmExit}
                      >
                        Salir de venta
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </SuggestedDataProvider>
    </SelectedOfferProvider>
  );
};

export default PurchaseFlow;
